import { HeadingLarge, HeadingMedium, SearchInput, SideModal, theme } from '@arnold/common';
import styled from '@emotion/styled';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ResponsiveSwitcher } from '../../../components/ResponsiveSwitcher';

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onReset: () => void;
  title: string;
  hasActiveFilters: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  showExpandCollapse?: boolean;
  allExpanded?: boolean;
  onToggleExpandAll?: () => void;
  children: React.ReactNode;
}

export const FilterDrawer = ({
  open,
  onClose,
  onConfirm,
  onReset,
  title,
  hasActiveFilters,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  showExpandCollapse,
  allExpanded,
  onToggleExpandAll,
  children,
}: FilterDrawerProps) => {
  const { t } = useTranslation('newReport');

  const headerContent = (
    <>
      <DrawerHeaderRow>
        <ResponsiveSwitcher
          desktop={
            <HeadingLarge margin="0" bold>
              {title}
            </HeadingLarge>
          }
          mobile={
            <StyledHeadingMedium margin="0" bold>
              {title}
            </StyledHeadingMedium>
          }
        />
        {hasActiveFilters && (
          <Button variant="outline-primary" size="sm" onClick={onReset}>
            {t('resetFilter')}
          </Button>
        )}
      </DrawerHeaderRow>
    </>
  );

  return (
    <SideModal
      opened={open}
      onCloseAction={onClose}
      header={headerContent}
      primaryButton={{ label: t('confirmFilters'), onClick: onConfirm }}
      secondaryButton={{ label: t('cancel'), onClick: onClose }}
      wide
      backgroundColor={theme.background.primary}
    >
      <div>
        {searchPlaceholder && onSearchChange && (
          <SearchContainer>
            <SearchInput
              value={searchValue ?? ''}
              onChange={onSearchChange}
              placeholder={searchPlaceholder}
              delay={200}
              hideSearchIcon
            />
          </SearchContainer>
        )}

        {showExpandCollapse && onToggleExpandAll && (
          <ExpandCollapseBar>
            <ExpandCollapseLink onClick={onToggleExpandAll}>
              {allExpanded ? t('collapseAll') : t('expandAll')}
            </ExpandCollapseLink>
          </ExpandCollapseBar>
        )}
        {children}
      </div>
    </SideModal>
  );
};

const DrawerHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.f};
  padding-bottom: ${theme.spacing.d};
  padding-top: ${theme.spacing.f};
`;

const SearchContainer = styled.div`
  padding-top: ${theme.spacing.g};
`;

const ExpandCollapseBar = styled.div`
  padding: ${theme.spacing.f} 0;
  border-bottom: ${theme.spacing.a} solid ${theme.colors.borderSeparator.default};
`;

const ExpandCollapseLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.actionPrimary.default};
  font-size: ${theme.typography.body.medium.regular.fontSize};
  padding: 0;

  &:hover {
    color: ${theme.colors.actionPrimary.hover};
    text-decoration: underline;
  }

  &:focus-visible {
    box-sizing: border-box;
    outline: ${theme.spacing.a} solid ${theme.colors.brand.primary};
    border-radius: ${theme.spacing.c};
  }
`;

const StyledHeadingMedium = styled(HeadingMedium)`
  line-height: 33px;
`;
